<script setup lang="ts">
import IconRestore from '~icons/mdi/restore'
import IconPill from '~icons/mdi/pill'
import IconNeedle from '~icons/mdi/needle'
import IconPetFood from '~icons/mdi/food-apple-outline'
import IconClinic from '~icons/mdi/office-building-marker-outline'
import IconClinicChain from '~icons/mdi/paw'
import IconCampaigns from '~icons/mdi/bullhorn-outline'

const route = useRoute()
const orderHistory = useOrderHistoryStore()
const userStore = useUserStore()
const categoryMenuItems = useCategoryMenuItems()
const localePath = useLocalePath()

const categoryByEnTerm = computed(() => Object.fromEntries(
  categoryMenuItems.value.map(cat => [cat.enTerm, cat]),
))

const campaignsTo = computed(() => localePath('/campaigns'))

function isItemActive(to: string, extraPrefixes: string[] = []) {
  return [to, ...extraPrefixes].some(prefix => (
    route.path === prefix || route.path.startsWith(`${prefix}/`)
  ))
}

function itemClass(to: string, extra?: { spaced?: boolean, prefixes?: string[] }) {
  return {
    'ms-2': extra?.spaced,
    'nav-bar__item--active': isItemActive(to, extra?.prefixes),
  }
}

function labelClass(to: string, isBold?: boolean, prefixes?: string[]) {
  return isBold || isItemActive(to, prefixes) ? 'font-weight-bold' : 'font-weight-regular'
}
</script>

<template>
  <div class="nav-bar d-none d-md-flex align-center bg-white">
    <v-container class="d-flex justify-space-between align-center pa-0 h-100">
      <div class="d-flex align-center overflow-x-auto">
        <v-btn
          v-if="orderHistory.totalOrders > 0"
          variant="text"
          color="black"
          rounded="0"
          :to="localePath('/order-history')"
          class="text-none text-body-1 px-2 nav-bar__item"
          :class="itemClass(localePath('/order-history'))"
          data-nav-id="nav_order_history"
        >
          <v-icon size="20" class="mr-1">
            <IconRestore />
          </v-icon>
          <span :class="labelClass(localePath('/order-history'), true)">
            {{ $t('orderHistory.title') }}
          </span>
        </v-btn>

        <v-divider
          v-if="userStore.data?.clinicChain && orderHistory.totalOrders > 0"
          vertical
          color="vm-grey-6"
          thickness="2"
          class="mx-2 align-self-center border-opacity-100 nav-bar__divider"
        />
        <v-btn
          v-if="userStore.data?.clinicChain"
          variant="text"
          color="black"
          rounded="0"
          :to="localePath('/clinic-chain')"
          class="text-none text-body-1 px-2 nav-bar__item"
          :class="itemClass(localePath('/clinic-chain'))"
          data-nav-id="nav_clinic_chain"
        >
          <v-icon size="20" class="mr-1 d-none d-lg-inline-flex">
            <IconClinicChain />
          </v-icon>
          <span :class="labelClass(localePath('/clinic-chain'), true)">
            {{ userStore.data.clinicChain.name }}
          </span>
        </v-btn>

        <v-divider
          v-if="categoryByEnTerm.Pharma && (orderHistory.totalOrders > 0 || userStore.data?.clinicChain)"
          vertical
          color="vm-grey-6"
          thickness="2"
          class="mx-2 align-self-center border-opacity-100 nav-bar__divider"
        />
        <v-btn
          v-if="categoryByEnTerm.Pharma"
          variant="text"
          color="black"
          rounded="0"
          :to="categoryByEnTerm.Pharma.to"
          class="text-none text-body-1 px-2 nav-bar__item"
          :class="itemClass(categoryByEnTerm.Pharma.to)"
          data-nav-id="nav_pharma"
        >
          <v-icon size="20" class="mr-1 d-none d-lg-inline-flex">
            <IconPill />
          </v-icon>
          <span :class="labelClass(categoryByEnTerm.Pharma.to)">
            {{ categoryByEnTerm.Pharma.label }}
          </span>
        </v-btn>

        <v-btn
          v-if="categoryByEnTerm.Consumables"
          variant="text"
          color="black"
          rounded="0"
          :to="categoryByEnTerm.Consumables.to"
          class="text-none text-body-1 px-2 nav-bar__item"
          :class="itemClass(categoryByEnTerm.Consumables.to, { spaced: true })"
          data-nav-id="nav_consumables"
        >
          <v-icon size="20" class="mr-1 d-none d-lg-inline-flex">
            <IconNeedle />
          </v-icon>
          <span :class="labelClass(categoryByEnTerm.Consumables.to)">
            {{ categoryByEnTerm.Consumables.label }}
          </span>
        </v-btn>

        <v-btn
          v-if="categoryByEnTerm['Pet Food']"
          variant="text"
          color="black"
          rounded="0"
          :to="categoryByEnTerm['Pet Food'].to"
          class="text-none text-body-1 px-2 nav-bar__item"
          :class="itemClass(categoryByEnTerm['Pet Food'].to, { spaced: true })"
          data-nav-id="nav_pet_food"
        >
          <v-icon size="20" class="mr-1 d-none d-lg-inline-flex">
            <IconPetFood />
          </v-icon>
          <span :class="labelClass(categoryByEnTerm['Pet Food'].to)">
            {{ categoryByEnTerm['Pet Food'].label }}
          </span>
        </v-btn>

        <v-divider
          v-if="orderHistory.totalOrders > 0 || userStore.data?.clinicChain || categoryByEnTerm.Pharma || categoryByEnTerm.Consumables || categoryByEnTerm['Pet Food']"
          vertical
          color="vm-grey-6"
          thickness="2"
          class="mx-2 align-self-center border-opacity-100 nav-bar__divider"
        />
        <v-btn
          variant="text"
          color="black"
          rounded="0"
          :to="localePath('/suppliers')"
          class="text-none text-body-1 px-2 nav-bar__item"
          :class="itemClass(localePath('/suppliers'), { prefixes: [localePath('/supplier')] })"
          data-nav-id="nav_suppliers"
        >
          <v-icon size="20" class="mr-1 d-none d-lg-inline-flex">
            <IconClinic />
          </v-icon>
          <span :class="labelClass(localePath('/suppliers'), true, [localePath('/supplier')])">
            {{ $t('layout.menuSecondary.suppliers') }}
          </span>
        </v-btn>
      </div>

      <v-btn
        :to="campaignsTo"
        variant="flat"
        rounded="lg"
        color="red-50"
        :ripple="false"
        height="40"
        min-width="40"
        class="text-none text-body-1 font-weight-bold text-black flex-shrink-0 nav-bar__item"
        :class="{ 'nav-bar__item--active': isItemActive(campaignsTo, [localePath('/campaign')]) }"
        data-nav-id="nav_campaigns"
      >
        <v-icon
          :icon="IconCampaigns"
          size="20"
          class="mr-lg-2 nav-bar__campaigns-icon"
        />
        <span class="d-none d-lg-inline">
          {{ $t('layout.menuSecondary.campaigns') }}
        </span>
      </v-btn>
    </v-container>
  </div>
</template>

<style scoped>
.nav-bar {
  height: 56px;
  position: sticky;
  top: 80px;
  z-index: 100;
}

.nav-bar__divider {
  height: 20px;
}

.nav-bar__campaigns-icon {
  transform: rotate(-15deg);
}

.nav-bar :deep(.nav-bar__item.v-btn--active > .v-btn__overlay) {
  opacity: 0;
}

.nav-bar :deep(.nav-bar__item--active) {
  color: rgb(var(--v-theme-red-600)) !important;
  box-shadow: inset 0 -3px 0 0 rgb(var(--v-theme-red-600));
}
</style>
